import { keyBy, orderBy } from "lodash";
import moment from "moment";

import { getCycleWeeksForBackend } from "@features/mmm/utils/get_cycle_weeks";
import {
  type Forecast_me_mmm_forecast_prediction,
  type FetchCustomScenarioData_me_mmm_customScenarioData,
} from "@north-beam/nb-client-side";
import { ISO_DATE_FORMAT, type MMMClientCycle } from "@north-beam/nb-common";

import { type Kpi } from "../client-setting/types";

import { type BudgetMixInput } from "./types";

const TEMPLATE_DATE_FORMAT = "MMM DD, YYYY";

export function getPeriods(dateRange: {
  startDate: moment.Moment;
  endDate: moment.Moment;
}) {
  const weeks: moment.Moment[] = [];
  const startDate = moment(dateRange.startDate);

  if (startDate.day() !== 2) {
    startDate.startOf("week").add(2, "days");
  }

  while (startDate.isBefore(dateRange.endDate)) {
    weeks.push(moment(startDate));
    startDate.add(1, "week");
  }

  return weeks.map((week) => {
    const startOfWeek = week.format(TEMPLATE_DATE_FORMAT);
    const endOfWeek = moment(week)
      .add(1, "week")
      .subtract(1, "day")
      .format(TEMPLATE_DATE_FORMAT);

    return `${startOfWeek} - ${endOfWeek}`;
  });
}

export function formatScenarioModellingDataAsBudgetMix(
  scenarioModellingData: FetchCustomScenarioData_me_mmm_customScenarioData,
  predictionsByDate: Record<string, Forecast_me_mmm_forecast_prediction>,
  kpisByName: Record<string, Kpi>,
  chosenKpi: Kpi,
  selectedCycle: MMMClientCycle,
):
  | {
      data: BudgetMixInput;
      error: null;
    }
  | {
      data: null;
      error: string;
    } {
  const expectedCycleWeeks = getCycleWeeksForBackend(selectedCycle);
  const cycleWeeksByStartDate = keyBy(expectedCycleWeeks, "startDate");
  const spendsByDate = scenarioModellingData.data.reduce((acc, curr) => {
    // There is guranteed to be "date"
    const date = moment(curr.find((d) => d.id === "date")?.value);
    const startDateString = date.format(ISO_DATE_FORMAT);
    const endDateString = date.add(7, "days").format(ISO_DATE_FORMAT);
    const flattenedSpend = curr
      .filter((d) => d.id !== "date")
      .map((d) => {
        return {
          spend: Number(d.value),
          data_column_name: d.id,
        };
      });

    acc[startDateString] = {
      start_date: startDateString,
      end_date: endDateString,
      spend_data: flattenedSpend,
    };

    return acc;
  }, {} as { [dateString: string]: Omit<BudgetMixInput["weekly_data"][number], "summary_data"> });

  const weeklyData: BudgetMixInput["weekly_data"] = orderBy(
    Object.keys(spendsByDate),
    "desc",
  ).map((startDateString) => {
    const weeklySpend = spendsByDate[startDateString];
    const weeklyForecast = predictionsByDate[startDateString];
    const accumulatedSpend = weeklySpend.spend_data.reduce(
      (acc, curr) => acc + curr.spend,
      0,
    );
    const kpiData = Object.keys(weeklyForecast.kpis).reduce((acc, kpiName) => {
      const item = weeklyForecast.kpis[kpiName];
      const kpi = kpisByName[kpiName];

      acc[kpiName] = {
        values: {
          high: item.high,
          low: item.low,
          median: item.value,
        },
        kpi_id: kpi.id,
      };
      return acc;
    }, {} as BudgetMixInput["weekly_data"][number]["summary_data"]["kpi_data"]);
    return {
      start_date: weeklySpend.start_date,
      end_date: weeklySpend.end_date,
      spend_data: weeklySpend.spend_data,
      summary_data: {
        spend: accumulatedSpend,
        kpi_data: kpiData,
      },
    };
  });

  if (weeklyData.length !== expectedCycleWeeks.length) {
    return {
      data: null,
      error: `Received ${weeklyData.length} weeks of data instead of the expected ${expectedCycleWeeks.length} weeks of data`,
    };
  }

  const weeklyDataFailedToMatchExpectedWeeks = weeklyData
    .map(
      (weekData) =>
        cycleWeeksByStartDate[weekData.start_date] &&
        cycleWeeksByStartDate[weekData.start_date].endDate ===
          weekData.end_date,
    )
    .some((didMatch) => !didMatch);

  if (weeklyDataFailedToMatchExpectedWeeks) {
    return {
      data: null,
      error:
        "Inputted weeks do not meet the requirement to be uploaded as a budget mix. Please see the dates in the template for proper week headers",
    };
  }

  const aggregateData = weeklyData.reduce(
    (acc, curr) => {
      const spendByColumnName = keyBy(acc.spend_data, "data_column_name");

      // spend data
      curr.spend_data.forEach((spend) => {
        if (!spendByColumnName[spend.data_column_name]) {
          spendByColumnName[spend.data_column_name] = {
            spend: 0,
            data_column_name: spend.data_column_name,
          };
        }
        spendByColumnName[spend.data_column_name].spend += spend.spend;
      });

      acc.spend_data = Object.values(spendByColumnName);
      acc.summary_data.spend += curr.summary_data.spend;

      Object.keys(curr.summary_data.kpi_data).forEach((kpi) => {
        const summary_kpi = curr.summary_data.kpi_data[kpi];
        if (!acc.summary_data.kpi_data[kpi]) {
          acc.summary_data.kpi_data[kpi] = {
            values: {
              high: 0,
              low: 0,
              median: 0,
            },
            kpi_id: summary_kpi.kpi_id,
          };
        }

        acc.summary_data.kpi_data[kpi].values.high += summary_kpi.values.high;
        acc.summary_data.kpi_data[kpi].values.low += summary_kpi.values.low;
        acc.summary_data.kpi_data[kpi].values.median +=
          summary_kpi.values.median;
      });

      return acc;
    },
    {
      spend_data: [],
      summary_data: {
        kpi_data: {},
        spend: 0,
      },
    } as BudgetMixInput["aggregated_data"],
  );

  const budgetMixPayload: BudgetMixInput = {
    weekly_data: weeklyData,
    aggregated_data: aggregateData,
    kpi_name: chosenKpi.kpi_name,
  };

  return {
    data: budgetMixPayload,
    error: null,
  };
}
